/* PJC v10 — blocs de contenu produit (chantier C).
 *
 * Module ES (type="module") chargé par blocks/pjc-size-guide.liquid et blocks/pjc-faq.liquid : un module n'est exécuté
 * qu'une seule fois par document, même si plusieurs blocs le référencent.
 *
 * - <pjc-size-guide> : bouton + <dialog> natif (showModal : focus piégé, Échap, arrière-plan inerte).
 *   Fermeture : bouton, clic sur l'arrière-plan, Échap. Le focus revient au déclencheur. Défilement de la page verrouillé.
 * - Ouverture depuis la FAQ : tout élément portant [data-pjc-open-size-guide] ouvre le guide de la même section
 *   (délégation d'événements enregistrée une seule fois).
 */

const SCROLL_LOCK_CLASS = 'pjc-scroll-lock';
const BOUND_KEY = Symbol.for('pjc.content.bound');

/** Propriétaires actuels du verrouillage de défilement (plusieurs dialogues possibles, jamais deux ouverts en pratique). */
const scrollLockOwners = new Set();

/**
 * @param {object} owner
 */
function lockScroll(owner) {
  scrollLockOwners.add(owner);
  document.documentElement.classList.add(SCROLL_LOCK_CLASS);
}

/**
 * @param {object} owner
 */
function unlockScroll(owner) {
  scrollLockOwners.delete(owner);
  if (scrollLockOwners.size === 0) document.documentElement.classList.remove(SCROLL_LOCK_CLASS);
}

class PjcSizeGuide extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {HTMLElement | null} */
  #opener = null;

  /** @returns {HTMLDialogElement | null} */
  get #dialog() {
    const dialog = this.querySelector('dialog[data-pjc-sg-dialog]');
    return dialog instanceof HTMLDialogElement ? dialog : null;
  }

  connectedCallback() {
    const dialog = this.#dialog;
    // Navigateur sans <dialog> modal : le bouton reste inactif (masqué par le CSS tant que l'élément n'est pas défini).
    if (!dialog || typeof dialog.showModal !== 'function') return;

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    const trigger = this.querySelector('[data-pjc-sg-trigger]');
    trigger?.addEventListener('click', () => this.open(trigger instanceof HTMLElement ? trigger : undefined), { signal });

    // Clic sur l'arrière-plan : la cible est le <dialog> lui-même (le panneau le remplit entièrement).
    dialog.addEventListener(
      'click',
      (event) => {
        if (event.target === dialog) dialog.close();
      },
      { signal }
    );

    for (const button of dialog.querySelectorAll('[data-pjc-sg-close]')) {
      button.addEventListener('click', () => dialog.close(), { signal });
    }

    dialog.addEventListener('close', this.#handleClose, { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#opener = null;
    // Un dialogue modal retiré du DOM ne déclenche pas « close » : on libère le verrou nous-mêmes.
    unlockScroll(this);
  }

  /**
   * Ouvre le guide.
   * @param {Element} [opener] - Élément qui rendra le focus à la fermeture (par défaut l'élément actif).
   */
  open(opener) {
    const dialog = this.#dialog;
    if (!dialog || dialog.open || typeof dialog.showModal !== 'function') return;

    const active = document.activeElement;
    this.#opener = opener instanceof HTMLElement ? opener : active instanceof HTMLElement ? active : null;

    lockScroll(this);
    try {
      dialog.showModal();
    } catch {
      unlockScroll(this);
      this.#opener = null;
      return;
    }

    const body = dialog.querySelector('.pjc-sg__body');
    if (body) body.scrollTop = 0;
  }

  #handleClose = () => {
    unlockScroll(this);

    const opener = this.#opener;
    this.#opener = null;
    if (opener?.isConnected) opener.focus({ preventScroll: true });
  };
}

if (!customElements.get('pjc-size-guide')) customElements.define('pjc-size-guide', PjcSizeGuide);

// Ouverture du guide depuis un bouton de la FAQ (ou de tout autre bloc) : une seule écoute pour le document.
if (!document[BOUND_KEY]) {
  document[BOUND_KEY] = true;

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const opener = event.target.closest('[data-pjc-open-size-guide]');
    if (!opener) return;

    const scope = opener.closest('.shopify-section') ?? document;
    const guide = scope.querySelector('pjc-size-guide') ?? document.querySelector('pjc-size-guide');
    if (!(guide instanceof PjcSizeGuide)) return;

    event.preventDefault();
    guide.open(opener);
  });
}
